import PausedIcon from "@assets/icons/pause.svg?react";
import noImageThumbnail from "@src/app/assets/no-image-thumbnail.jpg";
import TimeCode from "@src/app/components/TimeCode/TimeCode";
import cx from "classnames";
import type { MouseEvent, ReactElement } from "react";

import VideoPlayingIcon from "../VideoPlayingIcon/VideoPlayingIcon";

type Props = Readonly<{
  title: string;
  thumbnailSrc?: string;
  youtubeId: string;
  youtubeSrc: string;
  timeCode: string;
  isPlaying?: boolean;
  isPaused?: boolean;
  isEnded?: boolean;
  isSelected?: boolean;
  onClick?: (event: MouseEvent<HTMLDivElement>) => void;
}>;

export default function VideoWidget({
  title,
  thumbnailSrc,
  youtubeId,
  youtubeSrc,
  timeCode,
  isPlaying = false,
  isPaused,
  isEnded,
  isSelected = false,
  onClick,
  ...props
}: Props): ReactElement {
  const youtubeThumbnail = `https://img.youtube.com/vi/${youtubeId}/hqdefault.jpg`;

  return (
    <div
      id="video-widget"
      data-testid="video-widget"
      className={cx(
        "group grid items-start grid-cols-7 gap-3 cursor-pointer px-3 py-4 hover:bg-inverse-secondary transition-all ease-in-out",
        {
          "bg-inverse-secondary": isSelected,
          "bg-inverse": !isSelected,
        },
      )}
      data-url={youtubeSrc}
      onClick={onClick}
      role="presentation"
      {...props}
    >
      <div className="relative col-span-3">
        <img
          src={thumbnailSrc || youtubeThumbnail || noImageThumbnail}
          alt={title}
          className="size-full object-cover"
          style={{ aspectRatio: "3 / 2" }}
        />
        {isSelected && isPlaying && (
          <div className="absolute top-0 flex size-full items-center justify-center text-white">
            <VideoPlayingIcon
              className="size-[60px] rounded-full bg-icon-opacity-60"
              data-testid="video-playing-icon"
            />
          </div>
        )}

        {isSelected && isPaused && (
          <div className="absolute top-0 flex size-full items-center justify-center">
            <PausedIcon className="size-[60px] rounded-full bg-icon-opacity-60 p-4 text-white" />
          </div>
        )}

        {(!isSelected || isEnded) && (
          <TimeCode
            className="absolute bottom-0 right-0 m-2"
            time={timeCode}
            data-testid="time-code"
          />
        )}
      </div>

      <p className="col-span-4 line-clamp-3 text-body-2 text-primary-on-dark group-hover:underline">
        {title}
      </p>
    </div>
  );
}
